import React, { useState, useEffect, useRef } from "react";
import {
  Link,
  useParams,
  useNavigate,
  useSearchParams,
  useOutletContext,
} from "react-router-dom";
import api from "../../api";
import "./AdminPanel.css";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ChevronDown,
  ChevronUp,
  Database,
  Users,
  Building2,
  Wallet,
  TrendingUp,
  DollarSign,
  History,
  Activity,
  FileText,
  Lightbulb,
  MessageSquare,
  Cloud,
  Upload,
  Key,
  ArrowUpDown,
  Eye,
  Edit,
  Save,
  X,
  Calendar,
  PlayCircle,
  Loader2,
  CheckCircle,
  XCircle,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { formatTimeAgo, formatTimeUntil } from "../../lib/utils";

// Helper to format table name for display
const formatTableName = (tableName) => {
  return tableName
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

// Get table info - fully dynamic based on table name
const getTableInfo = (tableName) => {
  return {
    name: formatTableName(tableName),
    icon: Database,
    color: "gray",
  };
};

// Format value based on type
const formatValue = (value, key) => {
  if (value === null || value === undefined) return "-";

  // Check if this is a datetime field (now all datetime columns end with _at)
  if (key.includes("date") || key.includes("_at")) {
    try {
      return new Date(value).toLocaleString();
    } catch {
      return value;
    }
  }

  if (
    key.includes("balance") ||
    key.includes("value") ||
    key.includes("price") ||
    key.includes("amount") ||
    key.includes("pnl")
  ) {
    if (typeof value === "number") {
      const formatted = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(value);
      if (key.includes("pnl") || key.includes("gain_loss")) {
        return (
          <span
            className={
              value >= 0
                ? "text-green-600 dark:text-green-400"
                : "text-red-600 dark:text-red-400"
            }
          >
            {formatted}
          </span>
        );
      }
      return formatted;
    }
  }

  if (typeof value === "boolean") {
    return value ? (
      <Badge variant="default">Yes</Badge>
    ) : (
      <Badge variant="secondary">No</Badge>
    );
  }

  if (
    key === "status" ||
    key === "source" ||
    key === "transaction_type" ||
    key === "tax_status"
  ) {
    return <Badge variant="outline">{value}</Badge>;
  }

  if (typeof value === "string" && value.length > 50) {
    return <span title={value}>{value.substring(0, 50)}...</span>;
  }

  return value;
};

// Check if value is a reference ID
const isReferenceField = (key) => {
  return key.endsWith("_id") && key !== "id";
};

// Check if field is a ticker that should link to securities
const isTickerField = (key, tableName) => {
  // Link ticker fields from holdings, transactions, holding_snapshots, etc. to securities
  return key === "ticker" && tableName !== "securities";
};

// Get reference table name
const getReferenceTable = (key, foreignKeys) => {
  // First, check if we have dynamic foreign key metadata
  if (foreignKeys && foreignKeys[key]) {
    return foreignKeys[key];
  }

  // Fallback to hardcoded mappings for backward compatibility
  if (key === "user_id") return "users";
  if (key === "account_id") return "accounts";
  if (key === "institution_id") return "institutions";
  if (key === "account_snapshot_id") return "account_snapshots";
  return null;
};

// Format schedule for display
const formatSchedule = (task) => {
  if (!task.enabled) return "Disabled";
  if (task.cron) return task.cron;
  return "Event-driven";
};

// Status badge component for job status
const StatusBadge = ({ status }) => {
  switch (status) {
    case "succeeded":
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
          <CheckCircle className="h-3 w-3" />
          Succeeded
        </span>
      );
    case "failed":
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
          <XCircle className="h-3 w-3" />
          Failed
        </span>
      );
    case "doing":
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
          <Loader2 className="h-3 w-3 animate-spin" />
          Running
        </span>
      );
    case "todo":
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
          Queued
        </span>
      );
    default:
      return (
        <span className="text-xs text-muted-foreground">{status || "-"}</span>
      );
  }
};

const AdminPanel = () => {
  const { table, recordId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState([]);
  const [record, setRecord] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editedRecord, setEditedRecord] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({});
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [sortField, setSortField] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("DESC");
  const perPage = 25;
  const { availableTables } = useOutletContext() || { availableTables: [] };
  const [foreignKeys, setForeignKeys] = useState({});

  // Tasks state
  const [tasks, setTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [runningTasks, setRunningTasks] = useState({});
  const [taskResults, setTaskResults] = useState({});
  const [tasksSortField, setTasksSortField] = useState("name");
  const [tasksSortOrder, setTasksSortOrder] = useState("ASC");
  const pollIntervalsRef = useRef({});

  const currentTable = table;
  const tableInfo = table ? getTableInfo(currentTable) : null;
  const tickerFilter = searchParams.get("ticker");

  // Check if we're on the tasks page
  const isTasksPage = table === "tasks";

  // Cleanup poll intervals on unmount
  useEffect(() => {
    return () => {
      Object.values(pollIntervalsRef.current).forEach(clearInterval);
    };
  }, []);

  useEffect(() => {
    if (recordId) {
      fetchRecord();
    } else if (isTasksPage) {
      fetchTasks();
    } else if (table) {
      fetchTableData();
    } else {
      fetchStats();
    }
  }, [table, recordId, page, sortField, sortOrder]);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const response = await api.get("/admin/stats");
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTableData = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/admin/tables/${currentTable}`, {
        params: {
          page,
          per_page: perPage,
          sort_field: sortField,
          sort_order: sortOrder,
        },
      });
      setData(response.data.data);
      setTotal(response.data.total);
      setTotalPages(response.data.total_pages);
      setForeignKeys(response.data.foreign_keys || {});
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecord = async () => {
    setLoading(true);
    try {
      const [recordResponse, schemaResponse] = await Promise.all([
        api.get(`/admin/tables/${currentTable}/${recordId}`),
        api.get(`/admin/tables/${currentTable}/schema`),
      ]);
      setRecord(recordResponse.data);
      setForeignKeys(schemaResponse.data.foreign_keys || {});
    } catch (error) {
      console.error("Error fetching record:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "ASC" ? "DESC" : "ASC");
    } else {
      setSortField(field);
      setSortOrder("DESC");
    }
    setPage(1);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this record?")) return;

    try {
      await api.delete(`/admin/tables/${currentTable}/${id}`);
      if (recordId) {
        navigate(`/admin/${currentTable}`);
      } else {
        fetchTableData();
      }
    } catch (error) {
      console.error("Error deleting record:", error);
      alert("Failed to delete record");
    }
  };

  const handleEdit = () => {
    setEditedRecord({ ...record });
    setEditMode(true);
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setEditedRecord(null);
  };

  const handleSaveEdit = async () => {
    try {
      await api.put(`/admin/tables/${currentTable}/${recordId}`, editedRecord);
      setEditMode(false);
      fetchRecord();
    } catch (error) {
      console.error("Error updating record:", error);
      alert("Failed to update record");
    }
  };

  const handleFieldChange = (key, value) => {
    setEditedRecord({ ...editedRecord, [key]: value });
  };

  const fetchTasks = async () => {
    setTasksLoading(true);
    try {
      const response = await api.get("/admin/tasks");
      setTasks(response.data.tasks || []);
    } catch (error) {
      console.error("Error fetching tasks:", error);
    } finally {
      setTasksLoading(false);
    }
  };

  const handleTasksSort = (field) => {
    if (tasksSortField === field) {
      setTasksSortOrder(tasksSortOrder === "ASC" ? "DESC" : "ASC");
    } else {
      setTasksSortField(field);
      setTasksSortOrder("ASC");
    }
  };

  const sortTasks = (tasksArray) => {
    if (!tasksArray || tasksArray.length === 0) return tasksArray;

    return [...tasksArray].sort((a, b) => {
      let aVal, bVal;

      switch (tasksSortField) {
        case "name":
          aVal = a.name || "";
          bVal = b.name || "";
          break;
        case "task_name":
          aVal = a.task_name || "";
          bVal = b.task_name || "";
          break;
        case "schedule":
          aVal = a.cron || "zzz"; // Push null cron to end
          bVal = b.cron || "zzz";
          break;
        case "last_run":
          aVal = a.last_run?.scheduled_at
            ? new Date(a.last_run.scheduled_at).getTime()
            : 0;
          bVal = b.last_run?.scheduled_at
            ? new Date(b.last_run.scheduled_at).getTime()
            : 0;
          break;
        default:
          aVal = "";
          bVal = "";
      }

      if (typeof aVal === "number" && typeof bVal === "number") {
        return tasksSortOrder === "ASC" ? aVal - bVal : bVal - aVal;
      }

      const comparison = String(aVal).localeCompare(String(bVal));
      return tasksSortOrder === "ASC" ? comparison : -comparison;
    });
  };

  const pollJobStatus = (taskName, jobId) => {
    // Clear any existing poll for this task
    if (pollIntervalsRef.current[taskName]) {
      clearInterval(pollIntervalsRef.current[taskName]);
    }

    let attempts = 0;
    const maxAttempts = 30; // 60s timeout at 2s intervals

    const interval = setInterval(async () => {
      attempts++;

      try {
        const response = await api.get(
          `/admin/tasks/${taskName}/status/${jobId}`,
        );
        const status = response.data.status;

        if (status === "succeeded") {
          setTaskResults((prev) => ({
            ...prev,
            [taskName]: { status: "success", message: "Task completed" },
          }));
          setRunningTasks((prev) => ({ ...prev, [taskName]: false }));
          clearInterval(interval);
          delete pollIntervalsRef.current[taskName];
          fetchTasks();
        } else if (status === "failed") {
          setTaskResults((prev) => ({
            ...prev,
            [taskName]: { status: "error", message: "Task failed" },
          }));
          setRunningTasks((prev) => ({ ...prev, [taskName]: false }));
          clearInterval(interval);
          delete pollIntervalsRef.current[taskName];
          fetchTasks();
        }
        // else still "todo" or "doing" - keep polling
      } catch {
        // Ignore poll errors
      }

      if (attempts >= maxAttempts) {
        setTaskResults((prev) => ({
          ...prev,
          [taskName]: {
            status: "error",
            message: "Polling timeout - check worker logs",
          },
        }));
        setRunningTasks((prev) => ({ ...prev, [taskName]: false }));
        clearInterval(interval);
        delete pollIntervalsRef.current[taskName];
      }
    }, 2000);

    pollIntervalsRef.current[taskName] = interval;
  };

  const handleRunTask = async (taskName) => {
    setRunningTasks((prev) => ({ ...prev, [taskName]: true }));
    setTaskResults((prev) => ({ ...prev, [taskName]: null }));

    try {
      const response = await api.post(`/admin/tasks/${taskName}/run`);
      const jobId = response.data.job_id;

      setTaskResults((prev) => ({
        ...prev,
        [taskName]: { status: "queued", message: "Job queued..." },
      }));

      // Start polling for completion
      pollJobStatus(taskName, jobId);
    } catch (error) {
      setTaskResults((prev) => ({
        ...prev,
        [taskName]: {
          status: "error",
          message: "Failed to queue task",
          error: error.response?.data?.detail || error.message,
        },
      }));
      setRunningTasks((prev) => ({ ...prev, [taskName]: false }));
    }
  };

  const renderTableView = () => {
    if (loading) {
      return (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">Loading...</p>
          </div>
        </div>
      );
    }

    // Get all columns but hide certain ones by default to reduce width
    const allColumns = data.length > 0 ? Object.keys(data[0]) : [];
    const hiddenColumns = [
      // "created_at",
      "updated_at",
      // "last_login_at",
      // "price_updated_at",
    ];
    const columns = allColumns.filter((col) => !hiddenColumns.includes(col));

    // Filter data by ticker if provided in URL
    const displayData = tickerFilter
      ? data.filter((row) => row.ticker === tickerFilter)
      : data;

    return (
      <div className="flex-1 flex flex-col overflow-hidden bg-muted/30">
        <div className="border-b bg-card p-6 flex-shrink-0 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold tracking-tight">
                {tableInfo.name}
              </h2>
              <div className="flex items-center gap-3 mt-1.5">
                <p className="text-sm text-muted-foreground">
                  Showing {displayData.length} of {total} records
                </p>
                {tickerFilter && (
                  <Badge variant="secondary" className="font-mono">
                    Ticker: {tickerFilter}
                  </Badge>
                )}
              </div>
            </div>
            {tickerFilter && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/admin/${currentTable}`)}
              >
                <X className="h-4 w-4 mr-1" />
                Clear Filter
              </Button>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-auto">
          <div className="p-6">
            {columns.length === 0 ? (
              <div className="bg-card rounded-lg border p-12 text-center">
                <Database className="h-12 w-12 mx-auto mb-3 text-muted-foreground/50" />
                <p className="text-muted-foreground font-medium">
                  No data found
                </p>
              </div>
            ) : (
              <>
                <div className="bg-card rounded-lg border shadow-sm overflow-hidden">
                  <div className="overflow-x-auto">
                    <Table className="min-w-full">
                      <TableHeader>
                        <TableRow className="bg-muted/50 hover:bg-muted/50">
                          {columns.map((col) => (
                            <TableHead key={col} className="h-12 font-semibold">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 px-2 -ml-2 hover:bg-muted/80 font-semibold"
                                onClick={() => handleSort(col)}
                              >
                                {col
                                  .replace(/_/g, " ")
                                  .charAt(0)
                                  .toUpperCase() +
                                  col.replace(/_/g, " ").slice(1)}
                                {sortField === col ? (
                                  sortOrder === "ASC" ? (
                                    <ChevronUp className="ml-2 h-4 w-4" />
                                  ) : (
                                    <ChevronDown className="ml-2 h-4 w-4" />
                                  )
                                ) : (
                                  <ArrowUpDown className="ml-2 h-3 w-3 opacity-40" />
                                )}
                              </Button>
                            </TableHead>
                          ))}
                          <TableHead className="h-12 font-semibold text-right">
                            Actions
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {displayData.length === 0 ? (
                          <TableRow className="hover:bg-transparent">
                            <TableCell
                              colSpan={columns.length + 1}
                              className="text-center py-12"
                            >
                              <Database className="h-10 w-10 mx-auto mb-3 text-muted-foreground/30" />
                              <p className="text-muted-foreground font-medium">
                                {tickerFilter
                                  ? `No records found for ticker ${tickerFilter}`
                                  : "No data found"}
                              </p>
                            </TableCell>
                          </TableRow>
                        ) : (
                          displayData.map((row, idx) => (
                            <TableRow
                              key={row.id}
                              className={`transition-colors ${
                                idx % 2 === 0 ? "bg-background" : "bg-muted/20"
                              } hover:bg-muted/40`}
                            >
                              {columns.map((col) => (
                                <TableCell
                                  key={col}
                                  className={`py-3 ${
                                    col === "id" || col.endsWith("_id")
                                      ? "max-w-[150px]"
                                      : col.includes("description") ||
                                          col.includes("message")
                                        ? "max-w-[300px]"
                                        : "max-w-[200px]"
                                  }`}
                                >
                                  <div
                                    className="truncate"
                                    title={String(row[col] || "")}
                                  >
                                    {col === "id" ? (
                                      <Link
                                        to={`/admin/${currentTable}/${row[col]}`}
                                        className="text-primary hover:underline font-mono text-sm font-medium inline-flex items-center gap-1.5"
                                      >
                                        <Eye className="h-3 w-3" />
                                        {String(row[col]).substring(0, 8)}
                                      </Link>
                                    ) : col === "ticker" &&
                                      currentTable !== "securities" &&
                                      row[col] ? (
                                      <Link
                                        to={`/admin/securities?ticker=${encodeURIComponent(
                                          row[col],
                                        )}`}
                                        className="text-primary hover:underline font-semibold"
                                      >
                                        {row[col]}
                                      </Link>
                                    ) : isReferenceField(col) &&
                                      getReferenceTable(col, foreignKeys) ? (
                                      <Link
                                        to={`/admin/${getReferenceTable(col, foreignKeys)}/${
                                          row[col]
                                        }`}
                                        className="text-primary hover:underline font-mono text-sm"
                                      >
                                        {String(row[col]).substring(0, 8)}
                                      </Link>
                                    ) : (
                                      <span
                                        className={`${
                                          col.includes("balance") ||
                                          col.includes("value") ||
                                          col.includes("price")
                                            ? "font-semibold tabular-nums"
                                            : col.includes("date") ||
                                                col.includes("_at")
                                              ? "text-sm text-muted-foreground"
                                              : ""
                                        }`}
                                      >
                                        {formatValue(row[col], col)}
                                      </span>
                                    )}
                                  </div>
                                </TableCell>
                              ))}
                              <TableCell className="text-right">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() =>
                                    navigate(`/admin/${currentTable}/${row.id}`)
                                  }
                                  className="hover:bg-primary/10 hover:text-primary"
                                >
                                  <Eye className="h-4 w-4 mr-1" />
                                  View
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>

                {data.length > 0 && (
                  <div className="flex items-center justify-between mt-6 px-2">
                    <div className="text-sm font-medium text-muted-foreground">
                      Page <span className="text-foreground">{page}</span> of{" "}
                      <span className="text-foreground">{totalPages}</span>
                    </div>
                    <div className="flex gap-1.5">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(1)}
                        disabled={page === 1}
                      >
                        <ChevronsLeft className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(page - 1)}
                        disabled={page === 1}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(page + 1)}
                        disabled={page === totalPages}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(totalPages)}
                        disabled={page === totalPages}
                      >
                        <ChevronsRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Check if a value is JSON string
  const isJsonString = (str) => {
    if (typeof str !== "string") return false;
    try {
      const parsed = JSON.parse(str);
      return typeof parsed === "object" && parsed !== null;
    } catch {
      return false;
    }
  };

  // Pretty print JSON
  const prettyPrintJson = (str) => {
    try {
      return JSON.stringify(JSON.parse(str), null, 2);
    } catch {
      return str;
    }
  };

  const renderDetailView = () => {
    if (loading || !record) {
      return <div className="p-8 text-center">Loading...</div>;
    }

    return (
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="border-b bg-card p-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(`/admin/${currentTable}`)}
                className="mb-2"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Back to {tableInfo.name}
              </Button>
              <h2 className="text-2xl font-bold">Record Details</h2>
              <p className="text-sm text-muted-foreground">ID: {record.id}</p>
            </div>
            <div className="flex gap-2">
              {editMode ? (
                <>
                  <Button variant="outline" onClick={handleCancelEdit}>
                    <X className="h-4 w-4 mr-1" />
                    Cancel
                  </Button>
                  <Button onClick={handleSaveEdit}>
                    <Save className="h-4 w-4 mr-1" />
                    Save
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="outline" onClick={handleEdit}>
                    <Edit className="h-4 w-4 mr-1" />
                    Edit
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => handleDelete(record.id)}
                  >
                    Delete
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-auto">
          <div className="p-6 max-w-4xl">
            <div className="space-y-6">
              {Object.entries(editMode ? editedRecord : record).map(
                ([key, value]) => {
                  const isJson = isJsonString(value);

                  return (
                    <div
                      key={key}
                      className="grid grid-cols-3 gap-4 items-start"
                    >
                      <div className="font-medium text-sm text-muted-foreground">
                        {key.replace(/_/g, " ").toUpperCase()}
                      </div>
                      <div className="col-span-2">
                        {editMode &&
                        key !== "id" &&
                        !key.includes("created_at") &&
                        !key.includes("updated_at") ? (
                          <div>
                            {typeof value === "boolean" ? (
                              <select
                                value={value.toString()}
                                onChange={(e) =>
                                  handleFieldChange(
                                    key,
                                    e.target.value === "true",
                                  )
                                }
                                className="w-full px-3 py-2 border rounded-md bg-background"
                              >
                                <option value="true">Yes</option>
                                <option value="false">No</option>
                              </select>
                            ) : isJson ||
                              (typeof value === "string" &&
                                value.length > 100) ? (
                              <Textarea
                                value={
                                  isJson ? prettyPrintJson(value) : value || ""
                                }
                                onChange={(e) =>
                                  handleFieldChange(key, e.target.value)
                                }
                                rows={isJson ? 10 : 4}
                                className="font-mono text-xs"
                              />
                            ) : (
                              <Input
                                type={
                                  typeof value === "number" ? "number" : "text"
                                }
                                value={value || ""}
                                onChange={(e) =>
                                  handleFieldChange(key, e.target.value)
                                }
                              />
                            )}
                          </div>
                        ) : key === "ticker" &&
                          currentTable !== "securities" &&
                          value ? (
                          <Link
                            to={`/admin/securities?ticker=${encodeURIComponent(
                              value,
                            )}`}
                            className="text-primary hover:underline"
                            title={`View ${value} in Securities`}
                          >
                            {value}
                          </Link>
                        ) : isReferenceField(key) &&
                          getReferenceTable(key, foreignKeys) ? (
                          <Link
                            to={`/admin/${getReferenceTable(key, foreignKeys)}/${value}`}
                            className="text-primary hover:underline"
                          >
                            {value}
                          </Link>
                        ) : isJson ? (
                          <Textarea
                            value={prettyPrintJson(value)}
                            readOnly
                            rows={10}
                            className="font-mono text-xs bg-muted"
                          />
                        ) : (
                          <div className="break-words">
                            {formatValue(value, key)}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                },
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderOverview = () => {
    if (loading) {
      return <div className="p-8 text-center">Loading...</div>;
    }

    return (
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="border-b bg-card p-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold">Database Overview</h2>
              <p className="text-sm text-muted-foreground">
                Record counts for all tables
              </p>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-auto">
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {availableTables.map((tableName) => {
                const info = getTableInfo(tableName);
                const Icon = info.icon;
                const count = stats[tableName] || 0;
                return (
                  <Card
                    key={tableName}
                    className="cursor-pointer hover:bg-accent transition-colors"
                    onClick={() => navigate(`/admin/${tableName}`)}
                  >
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">
                        {info.name}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">
                        {count.toLocaleString()}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {count === 1 ? "record" : "records"}
                      </p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderTasksView = () => {
    if (tasksLoading) {
      return (
        <div className="p-8 text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p>Loading tasks...</p>
        </div>
      );
    }

    const SortHeader = ({ field, children, className = "" }) => (
      <button
        onClick={() => handleTasksSort(field)}
        className={`flex items-center gap-1 hover:text-foreground transition-colors font-medium ${className}`}
      >
        {children}
        {tasksSortField === field &&
          (tasksSortOrder === "ASC" ? (
            <ChevronUp className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          ))}
        {tasksSortField !== field && (
          <ArrowUpDown className="h-4 w-4 opacity-30" />
        )}
      </button>
    );

    return (
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="border-b bg-card p-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold">Background Tasks</h2>
              <p className="text-sm text-muted-foreground">
                Procrastinate task queue - run tasks manually or view schedules
              </p>
            </div>
            <Button onClick={fetchTasks} variant="outline" size="sm">
              Refresh
            </Button>
          </div>
        </div>

        <div className="flex-1 overflow-auto">
          <div className="p-6">
            {tasks.length === 0 ? (
              <div className="text-center py-12">
                <Calendar className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground">No tasks found.</p>
              </div>
            ) : (
              <div className="border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[200px]">
                        <SortHeader field="name">Name</SortHeader>
                      </TableHead>
                      <TableHead className="w-[160px]">
                        <SortHeader field="task_name">Task ID</SortHeader>
                      </TableHead>
                      <TableHead className="w-[180px]">
                        <SortHeader field="schedule">Schedule</SortHeader>
                      </TableHead>
                      <TableHead className="w-[200px]">
                        <SortHeader field="last_run">Last Run</SortHeader>
                      </TableHead>
                      <TableHead className="w-[150px]">Next Run</TableHead>
                      <TableHead className="w-[120px]">Action</TableHead>
                      <TableHead className="w-[250px]">Result</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortTasks(tasks).map((task) => {
                      const isRunning = runningTasks[task.task_name];
                      const result = taskResults[task.task_name];

                      return (
                        <TableRow key={task.task_name}>
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              <Calendar className="h-4 w-4 text-muted-foreground" />
                              {task.name}
                            </div>
                          </TableCell>
                          <TableCell>
                            <code className="px-1.5 py-0.5 bg-muted rounded text-xs">
                              {task.task_name}
                            </code>
                          </TableCell>
                          <TableCell className="text-sm">
                            {!task.enabled ? (
                              <Badge variant="secondary">Disabled</Badge>
                            ) : task.cron ? (
                              <code className="text-xs">{task.cron}</code>
                            ) : (
                              <span className="text-muted-foreground">
                                Event-driven
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-sm">
                            {task.last_run ? (
                              task.last_run.status === "succeeded" ? (
                                <span
                                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 cursor-default"
                                  title={
                                    task.last_run.completed_at
                                      ? new Date(
                                          task.last_run.completed_at,
                                        ).toLocaleString()
                                      : undefined
                                  }
                                >
                                  <CheckCircle className="h-3 w-3" />
                                  {task.last_run.completed_at
                                    ? formatTimeAgo(task.last_run.completed_at)
                                    : "Succeeded"}
                                </span>
                              ) : task.last_run.status === "failed" ? (
                                <span
                                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 cursor-default"
                                  title={
                                    task.last_run.completed_at
                                      ? new Date(
                                          task.last_run.completed_at,
                                        ).toLocaleString()
                                      : undefined
                                  }
                                >
                                  <XCircle className="h-3 w-3" />
                                  {task.last_run.completed_at
                                    ? formatTimeAgo(task.last_run.completed_at)
                                    : "Failed"}
                                </span>
                              ) : (
                                <StatusBadge status={task.last_run.status} />
                              )
                            ) : (
                              <span className="text-muted-foreground">
                                Never run
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-sm">
                            {task.next_run ? (
                              <span
                                className="text-muted-foreground cursor-default"
                                title={new Date(task.next_run).toLocaleString()}
                              >
                                {formatTimeUntil(task.next_run)}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {task.can_trigger ? (
                              <Button
                                onClick={() => handleRunTask(task.task_name)}
                                disabled={isRunning}
                                size="sm"
                                className="w-full"
                              >
                                {isRunning ? (
                                  <>
                                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                    Running
                                  </>
                                ) : (
                                  <>
                                    <PlayCircle className="h-3 w-3 mr-1" />
                                    Run
                                  </>
                                )}
                              </Button>
                            ) : (
                              <span className="text-xs text-muted-foreground">
                                Triggered by events
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            {result && (
                              <div
                                className={`p-2 rounded-md border text-xs ${
                                  result.status === "success"
                                    ? "bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800"
                                    : result.status === "queued"
                                      ? "bg-yellow-50 dark:bg-yellow-950 border-yellow-200 dark:border-yellow-800"
                                      : "bg-red-50 dark:bg-red-950 border-red-200 dark:border-red-800"
                                }`}
                              >
                                <div className="flex items-start gap-2">
                                  {result.status === "success" ? (
                                    <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                                  ) : result.status === "queued" ? (
                                    <Loader2 className="h-4 w-4 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5 animate-spin" />
                                  ) : (
                                    <XCircle className="h-4 w-4 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                                  )}
                                  <div className="flex-1 min-w-0">
                                    <p
                                      className={`font-medium ${
                                        result.status === "success"
                                          ? "text-green-900 dark:text-green-100"
                                          : result.status === "queued"
                                            ? "text-yellow-900 dark:text-yellow-100"
                                            : "text-red-900 dark:text-red-100"
                                      }`}
                                      title={result.message}
                                    >
                                      {result.message}
                                    </p>
                                    {result.error && (
                                      <p
                                        className="text-red-700 dark:text-red-300 mt-1"
                                        title={result.error}
                                      >
                                        {result.error}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (!table) return renderOverview();
  if (isTasksPage) return renderTasksView();
  if (recordId) return renderDetailView();
  return renderTableView();
};

export default AdminPanel;
